import React from "react";
import { motion } from "framer-motion";
import Reveal from "@/components/Reveal";
import { CheckCircle, ArrowRight } from "@phosphor-icons/react";

const INCLUDES = [
  "Incontro iniziale in azienda (mezza giornata)",
  "Analisi di bilancio degli ultimi 3 esercizi",
  "Mappatura processi e struttura organizzativa",
  "Diagnosi delle 3 leve prioritarie",
  "Report scritto con piano d'azione operativo",
  "Sessione di restituzione strategica dedicata",
];

export default function Diagnosi() {
  return (
    <section
      id="diagnosi"
      data-testid="section-diagnosi"
      className="relative py-32 md:py-40 lg:py-48 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute top-16 -left-4 md:left-10 font-display italic text-[14rem] md:text-[22rem] leading-none text-gold/[0.06] select-none pointer-events-none"
      >
        05
      </div>

      {/* soft gold blob */}
      <div aria-hidden className="absolute inset-0">
        <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-gold/[0.05] blur-[140px]" />
      </div>

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="max-w-3xl mb-16 md:mb-20">
          <Reveal>
            <div className="flex items-center gap-4 mb-8">
              <span className="w-10 h-px bg-gold" />
              <span
                className="text-[0.7rem] uppercase text-gold font-sans-crisp font-semibold"
                style={{ letterSpacing: "0.3em" }}
              >
                Capitolo 05 · Il Nostro Pacchetto Firma
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2
              data-testid="diagnosi-title"
              className="font-display font-medium tracking-tight leading-[1.02] text-4xl md:text-5xl lg:text-6xl text-ink"
            >
              Pacchetto <span className="italic-serif text-gold">Diagnosi Aziendale</span>.
            </h2>
          </Reveal>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="relative border-2 border-gold bg-navy-dark p-8 md:p-14 lg:p-20"
          data-testid="diagnosi-card"
        >
          {/* corner brackets to reinforce hand-crafted feel */}
          <span className="absolute -top-2 -left-2 w-4 h-4 border-t-2 border-l-2 border-gold" />
          <span className="absolute -top-2 -right-2 w-4 h-4 border-t-2 border-r-2 border-gold" />
          <span className="absolute -bottom-2 -left-2 w-4 h-4 border-b-2 border-l-2 border-gold" />
          <span className="absolute -bottom-2 -right-2 w-4 h-4 border-b-2 border-r-2 border-gold" />

          <div className="grid grid-cols-12 gap-8 lg:gap-16">
            <div className="col-span-12 lg:col-span-7">
              <p
                className="text-[0.7rem] uppercase text-gold/80 font-sans-crisp font-semibold mb-6"
                style={{ letterSpacing: "0.3em" }}
              >
                Un punto di partenza. Un metodo definito.
              </p>
              <h3 className="font-display text-3xl md:text-4xl lg:text-5xl text-ink leading-[1.1] mb-8">
                Una radiografia completa della tua azienda in{" "}
                <span className="italic-serif text-gold">2 settimane</span>.
              </h3>
              <p className="text-lg text-ink/75 leading-relaxed font-sans-crisp mb-10 max-w-xl">
                Un intervento chirurgico e strutturato: entriamo, analizziamo,
                consegniamo un piano scritto operativo. Nessuna slide di rimando.
                Solo priorità chiare e azioni concrete per liberare valore.
              </p>

              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 mb-10" data-testid="diagnosi-includes">
                {INCLUDES.map((item, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -12 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.5 }}
                    transition={{ duration: 0.6, delay: 0.05 * i, ease: [0.22, 1, 0.36, 1] }}
                    className="flex items-start gap-3 text-ink/80 font-sans-crisp"
                  >
                    <CheckCircle size={20} weight="light" className="text-gold shrink-0 mt-1" />
                    <span>{item}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            {/* Price column */}
            <div className="col-span-12 lg:col-span-5 lg:border-l lg:border-gold/25 lg:pl-14">
              <div className="flex flex-col h-full">
                <p
                  className="text-[0.7rem] uppercase text-gold/80 font-sans-crisp font-semibold mb-4"
                  style={{ letterSpacing: "0.3em" }}
                >
                  Investimento
                </p>
                <div className="flex items-baseline gap-2 mb-2">
                  <span
                    className="font-display italic text-gold text-6xl md:text-7xl lg:text-8xl leading-none"
                    data-testid="diagnosi-price"
                  >
                    2.500
                  </span>
                  <span className="font-display italic text-gold text-3xl">€</span>
                </div>
                <p
                  className="text-[0.75rem] uppercase text-ink/60 font-sans-crisp mb-10"
                  style={{ letterSpacing: "0.24em" }}
                >
                  + IVA · Una tantum · Fatturato in Italia
                </p>

                <div className="hairline my-2" />

                <ul className="my-8 space-y-3 text-sm text-ink/70 font-sans-crisp">
                  <li className="flex justify-between"><span>Durata</span><span className="text-gold">2 settimane</span></li>
                  <li className="flex justify-between"><span>Formato</span><span className="text-gold">On-site + Remote</span></li>
                  <li className="flex justify-between"><span>Deliverable</span><span className="text-gold">Report scritto</span></li>
                </ul>

                <a
                  href="mailto:marco@mdconsultingsas.com?subject=Pacchetto%20Diagnosi%20Aziendale"
                  data-testid="diagnosi-cta"
                  className="group mt-auto relative inline-flex items-center justify-center gap-3 bg-gold text-navy-dark hover:bg-gold-light transition-colors duration-500 px-8 py-5 uppercase font-sans-crisp font-semibold text-[0.78rem] overflow-hidden"
                  style={{ letterSpacing: "0.24em" }}
                >
                  <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
                  <span className="relative">Prenota ora</span>
                  <ArrowRight size={16} weight="bold" className="relative transition-transform duration-500 group-hover:translate-x-1" />
                </a>

                <p className="mt-4 text-[0.72rem] text-ink/50 font-sans-crisp text-center">
                  Risposta entro 24 ore lavorative.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
